/**
 * The call queue's filter bar as plain data: its shape, its empty value, and
 * whether anything in it is narrowing the list.
 *
 * React-free on purpose, so the one question with a consequence can be tested
 * under plain Node. "Is a filter on?" decides which empty state the queue
 * shows, and the two are opposite facts: "the autopilot has not found anyone
 * yet, go and switch it on" and "nobody matches what you asked for". Showing
 * the first to somebody who ticked "hot" plus "has e-mail" tells them the tool
 * is empty when it is full - which is what the queue did while it printed both
 * sentences at once.
 */
import type { LeadSeverity, LeadStatus } from "../../../lib/scanLeads";

export interface LeadFilterState {
  q: string;
  status: LeadStatus[];
  severity: LeadSeverity[];
  hasEmail: boolean;
  hasCompany: boolean;
}

export const EMPTY_LEAD_FILTERS: LeadFilterState = {
  q: "",
  status: [],
  severity: [],
  hasEmail: false,
  hasCompany: false,
};

/**
 * True when any part of the filter bar narrows the list.
 *
 * Every field is named rather than compared to `EMPTY_LEAD_FILTERS` as a whole,
 * so the test can walk the fields and prove a new one was not forgotten here.
 */
export function hasActiveLeadFilters(filters: LeadFilterState): boolean {
  return (
    filters.q.trim() !== "" ||
    filters.status.length > 0 ||
    filters.severity.length > 0 ||
    filters.hasEmail ||
    filters.hasCompany
  );
}
